// Core Types for ShowTracker App

// ── Contest Season (v4-dev) ───────────────────────────────────────────────────

export enum SeasonStatus {
  Planned = 'Planned',
  Active = 'Active',
  Completed = 'Completed',
  Archived = 'Archived',
}

export interface ContestSeason {
  seasonId: string;
  userId: string;
  name: string;
  startDate: string;
  endDate?: string;
  // Not a FK yet — raw Guid stored, FK added in later migration
  primaryShowId?: string;
  status: SeasonStatus;
  createdAt: string;
}

export enum ShowPriority {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Backup = 'Backup',
  TuneUp = 'TuneUp',
  Added = 'Added',
}

export enum ShowStatus {
  Planned = 'Planned',
  Active = 'Active',
  Completed = 'Completed',
  Archived = 'Archived',
}

// ── Alert Thresholds ─────────────────────────────────────────────────────────
export interface AlertThreshold {
  thresholdId: string;
  metricKey: string;          // e.g. "systolic_bp", "resting_hr", "glucose"
  unit?: string;
  sex: string;                // "male" | "female" | "any"
  context: string;            // "general" | "enhanced" | "contest_prep" | "offseason"
  phase?: string;             // "cut" | "maintenance" | "growth" | "peak_week" | null = any
  // High-value alerts: warningMin / criticalMin (value ≥ threshold triggers)
  // Low-value alerts:  warningMax / criticalMax (value ≤ threshold triggers)
  warningMin?: number;
  warningMax?: number;
  criticalMin?: number;
  criticalMax?: number;
  durationRequired: string;   // "single_reading" | "3_day_avg" | "7_day_avg"
  messageTemplate?: string;
  recommendedAction?: string;
  version: string;
  effectiveDate: string;
}

export enum PhaseType {
  Cruise = 'Cruise',
  MiniCut = 'MiniCut',
  Hold = 'Hold',
  Bridge = 'Bridge',
  Rebound = 'Rebound',
  GrowthBlast = 'GrowthBlast',
  ContestPrep = 'ContestPrep',
  Show = 'Show'
}

export enum CompoundType {
  InjectableAAS = 'InjectableAAS',
  OralAAS = 'OralAAS',
  Ancillary = 'Ancillary',
  Peptide = 'Peptide'
}

export interface Show {
  showId: string;
  userId: string;
  name: string;
  date: string;
  location: string;
  category: string;
  division?: string;
  targetWeight?: number;
  isActive: boolean;
  createdAt: string;
  phases?: Phase[];
  // Season support fields (v4-dev) — optional for backward compatibility
  seasonId?: string;
  showPriority?: ShowPriority;
  status?: ShowStatus;
}

export interface Phase {
  phaseId: string;
  showId?: string;
  seasonId?: string;
  phaseType: PhaseType;
  name: string;
  startDate: string;
  endDate: string;
  durationWeeks: number;
  isActive: boolean;
  sortOrder: number;
}

export interface DailyMetrics {
  metricId: string;
  userId: string;
  showId?: string;
  date: string;
  weight?: number;
  bodyFatPercentage?: number;
  leanMass?: number;
  height?: string;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  glucoseLevel?: number;
  sleepHours?: number;
  sleepQuality?: number;
  wellbeingScore?: number;
  notes?: string;

  // Wearable / activity fields (v3.5)
  stepsYesterday?: number;
  overnightHrv?: number;
  overnightRestingHeartRate?: number;

  // Gut health (v3.5)
  hadBowelMovement?: boolean;
  bowelMovementConsistency?: string; // "Normal" | "Loose" | "Liquid"

  // PED side effects (v3.5)
  pedSideEffects?: boolean;
  pedSideEffectsNotes?: string;

  createdAt: string;

  // Check-in photos (v3.6) — count returned by the list endpoint for the badge indicator
  photoCount?: number;
}

export interface CheckInPhoto {
  photoId: string;
  metricId: string;
  originalFileName?: string;
  contentType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

export interface PEDCompound {
  compoundId: string;
  name: string;
  shortName: string;
  type: CompoundType;
  unit: string;
}

export interface PEDCycle {
  cycleId: string;
  userId: string;
  showId?: string;
  seasonId?: string;
  phaseId?: string;
  startDate: string;
  endDate?: string;
  name: string;
  notes?: string;
  details: PEDCycleDetail[];
}

export interface PEDCycleDetail {
  cycleDetailId: string;
  cycleId: string;
  compoundId: string;
  compound?: PEDCompound;
  doseAmount: number;
  frequency: string;
  weeklyTotal: number;
  startWeek?: number;
  endWeek?: number;
}

export interface DailyPEDLog {
  logId: string;
  userId: string;
  date: string;
  compoundId: string;
  compound?: PEDCompound;
  doseAmount: number;
  notes?: string;
}

export interface DashboardData {
  userFirstName?: string;
  // Legacy field — kept for backward compat while migration is in progress
  activeShow?: Show;
  // Season fields (v4-dev) — null/empty when the show is not yet linked to a season
  activeSeason?: ContestSeason;
  primaryShow?: Show;
  allSeasonShows?: Show[];
  currentPhase?: Phase;
  latestMetrics?: DailyMetrics;
  activeCycle?: PEDCycle;
  countdown: {
    weeksRemaining: number;
    daysRemaining: number;
    phaseWeek: number;
  };
}

export interface TrendData {
  dates: string[];
  weights: number[];
  bodyFats: number[];
  leanMasses: number[];
  bloodPressureSystolic: number[];
  bloodPressureDiastolic: number[];
  glucose: number[];
  sleep: number[];
  wellbeing: number[];
}

export interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  authProvider: string;
  role: 'Bodybuilder' | 'Coach';
  isCoach: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface CoachInvite {
  inviteId: string;
  inviteCode: string;
  createdAt: string;
  expiresAt: string;
  isUsed: boolean;
  redeemedByCoachName?: string;
}

export interface CoachClient {
  relationshipId: string;
  athleteUserId: string;
  athleteName: string;
  athleteEmail: string;
  connectedSince: string;
}

export interface MyCoach {
  relationshipId: string;
  coachUserId: string;
  coachName: string;
  coachEmail: string;
  connectedSince: string;
}

// ── Purchase Calculator / Show Budget ────────────────────────────────────────

export interface CompoundPreference {
  preferenceId: string;
  compoundId: string;
  concentrationMgPerMl: number;
  vialSizeMl: number;
  pricePerVial: number;
}

export interface PurchaseSettings {
  bufferPercent: number;
  taxPercent: number;
  shippingCost: number;
}

export interface PurchasePreferencesResponse {
  settings: PurchaseSettings;
  compoundPreferences: CompoundPreference[];
}

export type BudgetCategory = 'DrugLoad' | 'Travel' | 'Tanning' | 'EntryFees' | 'Coaching' | 'Other';

export interface VialDetailItem {
  compound:     string;
  shortName:    string;
  vials:        number;
  pricePerVial: number;
  subtotal:     number;
}

// ── Messaging ────────────────────────────────────────────────────────────────

export type MessageContextType = 'General' | 'DailyCheckIn' | 'PEDLog' | 'Phase' | 'PEDCycle' | 'TrainingSession';

export interface Message {
  messageId:      string;
  relationshipId: string;
  senderUserId:   string;
  senderName:     string;
  senderIsCoach:  boolean;
  body:           string;
  sentAt:         string;
  isRead:         boolean;
  contextType?:   MessageContextType;
  contextId?:     string;
}

export interface SendMessage {
  relationshipId: string;
  body:           string;
  contextType?:   MessageContextType;
  contextId?:     string;
}

export interface UnreadMessageSummary {
  relationshipId: string;
  senderUserId:   string;
  senderName:     string;
  senderIsCoach:  boolean;
  unreadCount:    number;
  latestSnippet:  string;
  contextType?:   MessageContextType;
  contextId?:     string;
}

export interface AppAlert {
  alertId:         string;
  type:            'OrderReminder' | 'DailyCheckInReminder' | 'NewMessage' | 'TrainingPlanAssigned' | 'TrainingPlanUpdated' | 'PlanFileShared';
  title:           string;
  body:            string;
  cycleId?:        string;
  daysUntilStart?: number;
  /** NewMessage alerts: the relationship to open in /messages */
  relationshipId?: string;
  /** Contextual note alerts — navigate to the relevant page instead of /messages */
  contextType?:    MessageContextType;
  contextId?:      string;
  /** Sender info — used to build coach-view URLs when the receiver is a coach */
  senderUserId?:   string;
  senderName?:     string;
  senderIsCoach?:  boolean;
  acknowledged:    boolean;
}

export interface ShowBudgetItem {
  budgetItemId: string;
  showId: string;
  cycleId?: string;
  category: BudgetCategory;
  label: string;
  amount: number;
  notes?: string;
  /** JSON string — array of VialDetailItem, present on DrugLoad items saved by Purchase Calculator */
  vialDetails?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShowBudgetResponse {
  showId: string;
  items: ShowBudgetItem[];
  totalEstimate: number;
}

export interface UpsertShowBudgetItem {
  cycleId?: string;
  category: BudgetCategory;
  label: string;
  amount: number;
  notes?: string;
  vialDetails?: string;
}

export interface WeightPoint {
  date: string;
  weight: number;
}

export interface CompetitionCalendarEntry {
  athleteUserId: string;
  athleteName: string;
  relationshipId: string;
  showId: string;
  showName: string;
  showDate: string;
  location?: string;
  category?: string;
  division?: string;
  showPriority?: ShowPriority;
}

// ── Coach Athlete Notes ───────────────────────────────────────────────────────

export type CoachAthleteNoteTag =
  | 'HighAnxiety'
  | 'RoboticallyCompliant'
  | 'DrugSensitivity'
  | 'HighCompliance'
  | 'Inconsistent'
  | 'CheckInRisk'
  | 'Cautious';

export interface CoachAthleteNote {
  noteId: string;
  coachUserId: string;
  athleteUserId: string;
  relationshipId: string;
  content: string;
  tags: CoachAthleteNoteTag[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCoachNoteRequest {
  athleteUserId: string;
  relationshipId: string;
  content: string;
  tags: CoachAthleteNoteTag[];
}

export interface UpdateCoachNoteRequest {
  content: string;
  tags: CoachAthleteNoteTag[];
}

export interface ClientSummary {
  relationshipId: string;
  athleteUserId: string;
  athleteName: string;
  showName?: string;
  showDate?: string;
  weeksOut?: number;
  daysRemainder?: number;
  currentPhaseName?: string;
  currentPhaseType?: string;
  latestWeight?: number;
  targetWeight?: number;
  wellbeingScore?: number;
  lastCheckInDate?: string;
  latestBpSystolic?: number;
  latestBpDiastolic?: number;
  glucoseConsistentlyHigh: boolean;
  weightTrend: WeightPoint[];
  hasNotes: boolean;
}

export interface CoachPlanFile {
  planFileId: string;
  coachUserId: string;
  coachName: string;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  description?: string;
  uploadedAt: string;
}
